import { createInstance, dateReviver } from '@/api';
import {
    CreateOperationEntityDataMappingDto,
    OperationEntityDataMappingClient,
    OperationEntityDataMappingDetailDto,
    OperationEntityDetailDto,
    UpdateOperationEntityDataMappingDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useDeliveryProcessDefinitionEditStore } from '../delivery-process-definition/delivery-process-definition-edit.store';
import { copyOperationEntityValues } from '../../common/operation-entity.common';

export interface OperationEntityDataMappingStoreState {
    items?: { key: string; value: OperationEntityDataMappingDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new OperationEntityDataMappingClient('', createInstance(), dateReviver);

export const useOperationEntityDataMappingStore = defineStore('configuration-operation-entity-data-mapping-store', {
    state: (): OperationEntityDataMappingStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): OperationEntityDataMappingDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        updateOperationEntityUI(updatedOperationEntity: OperationEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyOperationEntityValues(updatedOperationEntity, entity.fromEntity);
                    copyOperationEntityValues(updatedOperationEntity, entity.toEntity);
                });
            });
        },
        async getByDefinition(
            definitionId: number,
            reloading: boolean = false
        ): Promise<OperationEntityDataMappingDetailDto[]> {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${definitionId}`;

            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
                return response;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('operationEntityDataMapping.detail.error.get.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
            return [];
        },
        async create(definitionId: number, dto: CreateOperationEntityDataMappingDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('operationEntityDataMapping.detail.success.create.title'));
                this.getByDefinition(definitionId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('operationEntityDataMapping.detail.error.create.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(definitionId: number, transformationId: number, dto: UpdateOperationEntityDataMappingDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(transformationId, dto);
                useAlertStore().setSuccess(i18n.global.t('operationEntityDataMapping.detail.success.update.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('operationEntityDataMapping.detail.error.update.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(definitionId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('operationEntityDataMapping.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('operationEntityDataMapping.detail.error.delete.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        async reset(definitionId: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.clearByDefinitionId(definitionId);
                useAlertStore().setSuccess(i18n.global.t('operationEntityDataMapping.detail.success.reset.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('operationEntityDataMapping.detail.error.reset.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        async generate(definitionId: number) {
            this.reloading = true;
            try {
                await client.generateByDefinitionId(definitionId);
                useAlertStore().setSuccess(i18n.global.t('operationEntityDataMapping.detail.success.generate.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('operationEntityDataMapping.detail.error.generate.title'),
                    error
                );
            } finally {
                this.reloading = false;
                useDeliveryProcessDefinitionEditStore().closeCreate();
            }
        },
        clear(definitionId: number) {
            const key = `definition-${definitionId}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
